import { useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LogOut, Menu, X, Globe, Search, ChevronRight, ChevronLeft,
} from "lucide-react";
import { Avatar, ThemeSwitcher } from "../ui";
import { T } from "../../constants";
import AnnouncementBell from "../features/announcements/AnnouncementBell";
import { NotificationBell } from "../features/notifications/NotificationBell";
import { useChatContext } from "../../context/ChatContext";

export default function AppShell({
  nav,
  setNav,
  navItems,
  user,
  lang,
  setLang,
  onLogout,
  children,
}) {
  const t = T[lang];
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const currentNavItem = useMemo(() => navItems.find((n) => n.id === nav), [navItems, nav]);
  const schoolInitial = useMemo(() =>
    user.school?.name ? user.school.name.charAt(0).toUpperCase() : "EP"
  , [user.school?.name]);

  const todayDate = useMemo(() =>
    new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
  , []);

  const userInitials = useMemo(() =>
    (user.name || user.id || "EP").slice(0, 2).toUpperCase()
  , [user.name, user.id]);

  const toggleSidebar = useCallback(() => setSidebarOpen(p => !p), []);
  const toggleLang = useCallback(() => setLang(lang === "en" ? "hi" : "en"), [lang, setLang]);

  const handleNavClick = useCallback((id) => {
    setNav(id);
    setMobileMenuOpen(false);
  }, [setNav]);

  const { totalUnread } = useChatContext();

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden font-sans">

      {/* ── Desktop Sidebar ─────────────────────────────────────────── */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.aside
            className="hidden lg:flex w-[260px] bg-slate-900 flex-col shrink-0"
            initial={{ x: -260, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -260, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {/* Logo */}
            <div className="px-5 pt-6 pb-5 border-b border-slate-800 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-indigo-500/25 shrink-0">
                  {schoolInitial}
                </div>
                <div className="overflow-hidden">
                  <div className="font-semibold text-base text-white leading-tight truncate">
                    {user.school?.name || (
                      <>eduPulse<span className="text-indigo-400">AI</span></>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wider mt-0.5 capitalize">
                    {user.role}
                  </div>
                </div>
              </div>
            </div>

            {/* Nav links */}
            <nav className="flex-1 px-3 py-4 overflow-y-auto space-y-0.5 min-h-0">
              {navItems.map((n, i) => {
                const Icon = n.ico;
                const isActive = nav === n.id;
                return (
                  <motion.button
                    key={n.id}
                    onClick={() => setNav(n.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 group ${
                      isActive
                        ? "bg-indigo-500/10 text-indigo-400"
                        : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/60"
                    }`}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.03, duration: 0.2 }}
                    whileHover={{ x: 2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors shrink-0 ${
                      isActive ? "bg-indigo-500/20" : "bg-slate-800 group-hover:bg-slate-700"
                    }`}>
                      <Icon size={17} strokeWidth={isActive ? 2.5 : 2} />
                    </span>
                    <span className="flex-1 text-left truncate">{n.lbl}</span>
                    {n.id === 'chat' && totalUnread > 0 && (
                      <span className="min-w-[18px] h-[18px] px-1 bg-indigo-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shrink-0">
                        {totalUnread > 9 ? '9+' : totalUnread}
                      </span>
                    )}
                    {isActive && (
                      <ChevronRight size={14} className="text-indigo-400 opacity-60 shrink-0" />
                    )}
                  </motion.button>
                );
              })}
            </nav>

            {/* User strip + logout */}
            <div className="px-4 py-4 border-t border-slate-800 shrink-0">
              <div className="flex gap-3 items-center mb-3">
                <Avatar
                  t={userInitials}
                  size={38}
                  bg="#334155"
                  className="rounded-xl shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-200 truncate">
                    {user.name || user.id}
                  </div>
                  <div className="text-xs text-slate-500 font-mono truncate">{user.id}</div>
                </div>
              </div>
              <button
                onClick={onLogout}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl
                           bg-slate-800 text-slate-300 text-sm font-medium
                           transition-all duration-200 hover:bg-rose-500/10 hover:text-rose-400 border-0 cursor-pointer"
              >
                <LogOut size={15} />
                {t.logout}
              </button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* ── Main area ───────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* Top header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 shrink-0 z-30">
          {/* Left */}
          <div className="flex items-center gap-3">
            <button
              onClick={toggleSidebar}
              className="hidden lg:flex w-9 h-9 rounded-lg items-center justify-center text-slate-500
                         transition-colors duration-200 hover:bg-slate-100 hover:text-slate-700 border-0 cursor-pointer"
              aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
            >
              {sidebarOpen ? <ChevronLeft size={18} /> : <Menu size={18} />}
            </button>
            <div className="hidden lg:block h-6 w-px bg-slate-200" />
            <div className="flex items-center gap-2">
              {currentNavItem?.ico && (
                <currentNavItem.ico size={16} className="text-slate-400" />
              )}
              <h1 className="text-base lg:text-lg font-semibold text-slate-800 truncate max-w-[160px] sm:max-w-none">
                {currentNavItem?.lbl || ""}
              </h1>
            </div>
          </div>

          {/* Right */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <div className="relative hidden sm:block">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 w-44 lg:w-56 pl-9 pr-3 rounded-lg border border-slate-200 bg-slate-50
                           text-sm text-slate-700 placeholder:text-slate-400
                           focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/20 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 border-0 cursor-pointer"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <AnnouncementBell user={user} />
            <NotificationBell />

            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 text-sm text-slate-500">
              <span className="font-mono text-xs">{todayDate}</span>
            </div>

            <div className="h-6 w-px bg-slate-200 hidden sm:block" />
            <ThemeSwitcher variant="button" />

            <button
              onClick={toggleLang}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-50 text-sm font-medium
                         text-slate-600 transition-colors duration-200 hover:bg-slate-100 border-0 cursor-pointer"
            >
              <Globe size={14} />
              <span className="uppercase text-xs tracking-wide hidden sm:inline">{lang}</span>
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-slate-50/50 pb-20 lg:pb-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={nav}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="px-2 sm:px-5 lg:px-8 py-5 sm:py-6 min-h-full"
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </main>

        {/* ── Mobile Bottom Navigation ─────────────────────────────── */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-slate-200 z-50 shadow-lg shadow-slate-900/5">
          <div className="flex items-stretch h-16">
            {/* Scrollable nav items */}
            <div className="flex-1 flex items-center h-full overflow-x-auto px-1 scrollbar-hide"
              style={{ scrollbarWidth: "none" }}
            >
              {navItems.map((item) => {
                const isActive = nav === item.id;
                const Icon = item.ico;
                return (
                  <button
                    key={item.id}
                    onClick={() => setNav(item.id)}
                    className={`flex flex-col items-center justify-center gap-0.5 px-2.5 py-2 mx-0.5 rounded-xl transition-all duration-200 min-w-[60px] shrink-0 select-none border-0 cursor-pointer ${
                      isActive
                        ? "bg-indigo-50 text-indigo-600"
                        : "text-slate-400 active:bg-slate-50"
                    }`}
                  >
                    <div className="relative">
                      <Icon size={19} strokeWidth={isActive ? 2.5 : 1.75} />
                      {item.id === 'chat' && totalUnread > 0 && (
                        <span className="absolute -top-1 -right-1.5 min-w-[14px] h-[14px] px-0.5 bg-indigo-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                          {totalUnread > 9 ? '9+' : totalUnread}
                        </span>
                      )}
                    </div>
                    <span className={`text-[9px] font-medium leading-none whitespace-nowrap mt-0.5 ${isActive ? "text-indigo-600" : "text-slate-400"}`}>
                      {item.lbl}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Divider */}
            <div className="w-px bg-slate-100 my-3 shrink-0" />

            {/* Profile / More button – always visible, shows logout */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className={`flex flex-col items-center justify-center gap-0.5 px-3 shrink-0 select-none border-0 cursor-pointer transition-all ${
                mobileMenuOpen ? "text-indigo-600" : "text-slate-400 active:bg-slate-50"
              }`}
            >
              <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs transition-all ${
                mobileMenuOpen ? "bg-indigo-600 text-white" : "bg-indigo-100 text-indigo-600"
              }`}>
                {userInitials}
              </div>
              <span className="text-[9px] font-medium leading-none whitespace-nowrap mt-0.5">More</span>
            </button>
          </div>
        </nav>

        {/* ── Mobile Bottom Sheet (profile + all nav + logout) ─────── */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <div className="lg:hidden fixed inset-0 z-[60]">
              {/* Backdrop */}
              <motion.div
                className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={() => setMobileMenuOpen(false)}
              />

              {/* Sheet */}
              <motion.div
                className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl max-h-[88vh] flex flex-col overflow-hidden shadow-2xl"
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", damping: 28, stiffness: 380 }}
              >
                {/* Drag handle */}
                <div className="flex justify-center pt-3 pb-1 shrink-0">
                  <div className="w-10 h-1 bg-slate-200 rounded-full" />
                </div>

                {/* User info */}
                <div className="px-5 pt-3 pb-4 border-b border-slate-100 shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center font-bold text-white text-base shadow-lg shadow-indigo-500/20 shrink-0">
                      {userInitials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 text-sm truncate">{user.name || user.id}</div>
                      <div className="text-xs text-slate-400 font-mono truncate">{user.id}</div>
                      <div className="text-[10px] text-indigo-500 font-bold uppercase tracking-wide mt-0.5 capitalize">{user.role}</div>
                    </div>
                    <button
                      onClick={() => setMobileMenuOpen(false)}
                      className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 border-0 cursor-pointer hover:bg-slate-200 shrink-0"
                    >
                      <X size={15} />
                    </button>
                  </div>
                </div>

                {/* All nav items */}
                <div className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5 min-h-0">
                  {navItems.map((item) => {
                    const Icon = item.ico;
                    const isActive = nav === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleNavClick(item.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition-all text-left border-0 cursor-pointer ${
                          isActive
                            ? "bg-indigo-50 text-indigo-600"
                            : "text-slate-600 hover:bg-slate-50 active:bg-slate-100"
                        }`}
                      >
                        <span className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                          isActive ? "bg-indigo-100 text-indigo-600" : "bg-slate-100 text-slate-500"
                        }`}>
                          <Icon size={16} strokeWidth={isActive ? 2.5 : 2} />
                        </span>
                        <span className="flex-1">{item.lbl}</span>
                        {isActive && <ChevronRight size={14} className="text-indigo-400 shrink-0" />}
                      </button>
                    );
                  })}
                </div>

                {/* Logout */}
                <div className="px-3 pt-2 pb-6 border-t border-slate-100 shrink-0">
                  <button
                    onClick={onLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-semibold text-rose-500 hover:bg-rose-50 active:bg-rose-100 transition-all border-0 cursor-pointer"
                  >
                    <span className="w-8 h-8 rounded-xl flex items-center justify-center bg-rose-50 text-rose-500 shrink-0">
                      <LogOut size={16} />
                    </span>
                    {t.logout}
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

      </div>
    </div>
  );
}
